import type {
  ClinicalFlag,
  FollowUp,
  MedicationRelatedProblem,
  PatientAllergy,
  PatientMedication,
  UUID,
} from "@dosa/types";
import { DEFAULT_CLINICAL_FLAG_CONFIG, type ClinicalFlagConfig } from "./config";

/**
 * DOSA clinical flags are deterministic and transparent by design: every flag here is a
 * simple, explainable rule over structured data. This module MUST NOT call an LLM, MUST NOT
 * infer a diagnosis, and MUST NOT recommend or change therapy. Flags only route attention;
 * a licensed pharmacist reviews and decides. See docs/CLINICAL_WORKFLOW.md and copilot.ts
 * in this package for the future (non-MVP) AI Copilot interface stub.
 */

export interface PatientClinicalContext {
  patientId: UUID;
  activeMedications: PatientMedication[];
  allergies: PatientAllergy[];
  /** True once a pharmacist has documented at least one monitoring parameter for this patient. */
  hasMonitoringPlan: boolean;
  openHighSeverityMrps: MedicationRelatedProblem[];
  upcomingFollowUps: FollowUp[];
  now?: Date;
}

function daysBetween(a: Date, b: Date): number {
  return Math.round((b.getTime() - a.getTime()) / (1000 * 60 * 60 * 24));
}

export function evaluatePolypharmacyFlag(
  ctx: Pick<PatientClinicalContext, "patientId" | "activeMedications">,
  config: ClinicalFlagConfig = DEFAULT_CLINICAL_FLAG_CONFIG,
): ClinicalFlag | null {
  const count = ctx.activeMedications.length;
  if (count < config.polypharmacyThreshold) return null;
  return {
    type: "POLYPHARMACY",
    severity: "WARNING",
    patientId: ctx.patientId,
    message: `Patient is on ${count} active medications (threshold: ${config.polypharmacyThreshold}). Consider a polypharmacy review.`,
    messageAr: `المريض يتناول ${count} أدوية نشطة (الحد: ${config.polypharmacyThreshold}). يُنصح بمراجعة تعدد الأدوية.`,
    context: { medicationCount: count, threshold: config.polypharmacyThreshold },
    generatedAt: new Date().toISOString(),
  };
}

export function evaluateMissingMedicationInfoFlag(
  ctx: Pick<PatientClinicalContext, "patientId" | "activeMedications">,
): ClinicalFlag | null {
  const incomplete = ctx.activeMedications.filter((m) => !m.dose || !m.frequency);
  if (incomplete.length === 0) return null;
  return {
    type: "MISSING_MEDICATION_INFO",
    severity: "WARNING",
    patientId: ctx.patientId,
    message: `${incomplete.length} medication(s) are missing dose or frequency information.`,
    messageAr: `${incomplete.length} دواء يفتقر إلى معلومات الجرعة أو التكرار.`,
    context: { incompleteCount: incomplete.length },
    generatedAt: new Date().toISOString(),
  };
}

export function evaluateMissingAllergyInfoFlag(
  ctx: Pick<PatientClinicalContext, "patientId" | "allergies">,
): ClinicalFlag | null {
  if (ctx.allergies.length > 0) return null;
  return {
    type: "MISSING_ALLERGY_INFO",
    severity: "INFO",
    patientId: ctx.patientId,
    message: "No allergy information has been recorded for this patient yet.",
    messageAr: "لم يتم تسجيل أي معلومات عن الحساسية لهذا المريض بعد.",
    generatedAt: new Date().toISOString(),
  };
}

export function evaluateMissingMonitoringInfoFlag(
  ctx: Pick<PatientClinicalContext, "patientId" | "hasMonitoringPlan" | "activeMedications">,
): ClinicalFlag | null {
  if (ctx.hasMonitoringPlan || ctx.activeMedications.length === 0) return null;
  return {
    type: "MISSING_MONITORING_INFO",
    severity: "INFO",
    patientId: ctx.patientId,
    message: "No monitoring parameters have been defined for this patient's care plan.",
    messageAr: "لم يتم تحديد معايير مراقبة لخطة رعاية هذا المريض.",
    generatedAt: new Date().toISOString(),
  };
}

export function evaluateFollowUpFlags(
  ctx: Pick<PatientClinicalContext, "patientId" | "upcomingFollowUps" | "now">,
  config: ClinicalFlagConfig = DEFAULT_CLINICAL_FLAG_CONFIG,
): ClinicalFlag[] {
  const now = ctx.now ?? new Date();
  const flags: ClinicalFlag[] = [];
  for (const fu of ctx.upcomingFollowUps) {
    if (fu.status !== "SCHEDULED") continue;
    const due = new Date(fu.dueDate);
    const diff = daysBetween(now, due);
    if (diff < 0) {
      flags.push({
        type: "FOLLOW_UP_OVERDUE",
        severity: "HIGH",
        patientId: ctx.patientId,
        message: `Follow-up was due on ${fu.dueDate} and is now overdue.`,
        messageAr: `كانت المتابعة مستحقة في ${fu.dueDate} وهي الآن متأخرة.`,
        context: { followUpId: fu.id, dueDate: fu.dueDate },
        generatedAt: now.toISOString(),
      });
    } else if (diff <= config.followUpDueWindowDays) {
      flags.push({
        type: "FOLLOW_UP_DUE",
        severity: "WARNING",
        patientId: ctx.patientId,
        message: `Follow-up is due on ${fu.dueDate}.`,
        messageAr: `المتابعة مستحقة في ${fu.dueDate}.`,
        context: { followUpId: fu.id, dueDate: fu.dueDate },
        generatedAt: now.toISOString(),
      });
    }
  }
  return flags;
}

export function evaluateHighPriorityReviewFlag(
  ctx: Pick<PatientClinicalContext, "patientId" | "openHighSeverityMrps">,
  config: ClinicalFlagConfig = DEFAULT_CLINICAL_FLAG_CONFIG,
): ClinicalFlag | null {
  const openHigh = ctx.openHighSeverityMrps.filter(
    (m) => config.highPrioritySeverities.includes(m.severity as "HIGH" | "CRITICAL") && m.status !== "RESOLVED",
  );
  if (openHigh.length === 0) return null;
  return {
    type: "HIGH_PRIORITY_REVIEW",
    severity: "HIGH",
    patientId: ctx.patientId,
    message: `${openHigh.length} unresolved high/critical-severity medication-related problem(s) require pharmacist review.`,
    messageAr: `يوجد ${openHigh.length} مشكلة دوائية غير محلولة بدرجة خطورة عالية/حرجة تتطلب مراجعة الصيدلي.`,
    context: { openHighSeverityCount: openHigh.length },
    generatedAt: new Date().toISOString(),
  };
}

/** Runs every deterministic flag rule and returns only the ones that fired. */
export function evaluateAllClinicalFlags(
  ctx: PatientClinicalContext,
  config: ClinicalFlagConfig = DEFAULT_CLINICAL_FLAG_CONFIG,
): ClinicalFlag[] {
  const flags: ClinicalFlag[] = [];
  const push = (f: ClinicalFlag | null) => {
    if (f) flags.push(f);
  };
  push(evaluatePolypharmacyFlag(ctx, config));
  push(evaluateMissingMedicationInfoFlag(ctx));
  push(evaluateMissingAllergyInfoFlag(ctx));
  push(evaluateMissingMonitoringInfoFlag(ctx));
  push(evaluateHighPriorityReviewFlag(ctx, config));
  flags.push(...evaluateFollowUpFlags(ctx, config));
  return flags;
}
